import { Injectable } from "@angular/core";
import { APIService } from "./APIService";
import { createPromiseClient, Interceptor } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
// Import from local npm package (resolved via tsconfig paths to ../protolib-js/build)
import { AssetService } from "@eventsuitemono/protolib/proto/assets_connect";
import { BuildPackRequest, RebuildCorePackRequest } from "@eventsuitemono/protolib/proto/assets_pb";

@Injectable({ providedIn: 'root' })
export class AssetsGrpcService {
  private transport = createConnectTransport({
    baseUrl: this.grpcBaseUrl(),
    // include credentials for auth cookie where applicable
    useBinaryFormat: false,
    credentials: "include",
    interceptors: [this.authInterceptor()],
  });

  private client = createPromiseClient(AssetService, this.transport);

  constructor(private api: APIService) {}

  private grpcBaseUrl(): string {
    // Determine the correct Connect endpoint base URL.
    // Production uses a dedicated subdomain connect.cubedcon.com.
    // Local development typically runs modern-api on localhost:50051.
    try {
      const loc = typeof window !== 'undefined' ? window.location : undefined;
      if (loc && loc.hostname.includes('localhost')) {
        return 'http://localhost:50051';
      }
    } catch {}

    const base = this.api.baseUrl; // e.g., https://api.cubedcon.com/v2

    // If we are pointing at the production API host, use the Connect host.
    if (base.startsWith('https://api.cubedcon.com')) {
      return 'https://connect.cubedcon.com';
    }

    // Fallback: strip trailing /v2 to get the host root
    return base.endsWith('/v2') ? base.substring(0, base.length - 3) : base;
  }

  private authInterceptor(): Interceptor {
    return (next) => async (req) => {
      // Attach bearer token header if APIService provides one via cookie
      const token = this.api.getToken?.();
      if (token) {
        req.header.set("Authorization", `Bearer ${token}`);
      }
      return await next(req);
    };
  }

  async rebuildCorePack() {
    // TS 5.4+ requires bracket access for PromiseClient index signature methods
    const res = await this.client["rebuildCorePack"](new RebuildCorePackRequest({}));
    return res;
  }

  async buildPack(categoryIds: number[], description?: string) {
    const req = new BuildPackRequest({ categoryId: categoryIds, description });
    const res = await this.client["buildPack"](req);
    return res;
  }
}
